import React, {FC} from "react"
import {Row, Col} from "react-bootstrap"

import tracking from "@irati-tracking/perks/perks"
import searchTracking from "@irati-tracking/search"
import id from "@irati/utils/id"
import FormattedMessage from "@irati/utils/lang"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import {Deal as IDeal, DealType, PromotionType} from "@irati/types"
import {determineDealColor, determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import {isDealUnavailable} from "@irati/deals/helpers/deals/isDealUnavailable"
import mainTheme from "@irati/ui/src/global/theme"
import {H6} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import Pill from "@irati/ui/src/Pill"
import Spacing from "@irati/ui/src/Spacing"
import PromotionQueryComponent from "@irati/promotions/apollo/query/PromotionQueryComponent"
import isRPDeal from "@irati/deals/helpers/deals/isRPDeal"

import ShareIconButton from "@irati/ui/src/ShareIconButton"
import {shouldDisplayDealAvatar} from "../../utils/deals/imageUtils"
import FavouriteIconButton, {FavouriteButtonOnChange} from "../Favourite/IconButton"
import {DealHighlightRow} from "../DealHighlight"
import DealPages from "../../enums/DealPages"
import DEAL_PAGES from "../../constants/dealPage"
import DealPromoInfo from "../DealPromoInfo"
import DealTileOnClick from "./DealTileOnClick"
import {StyledDeal} from "./styled/Deal"
import CardImage from "./CardImage"
import DealAvatar from "./styled/DealAvatar"
import {DealBody, DealHeader} from "./styled/DealBody"
import CardBadge from "./styled/CardBadge"
import DealHighlightTypes from "@irati-web/perks/src/constants/dealHighlightTypes"

interface Props extends WithBranding {
  deal: IDeal
  trackingData: {
    position: number
    sectionHeader: string
  }
  page: DealPages
  isFavouriteDeal: boolean
  className?: string
  useWalletDealCard?: boolean
  onShareClick?: (deal: IDeal) => void
  shareTitle?: string
  shareUrl?: string
}

const Deal: FC<Props> = (props) => {
  const {deal, page, trackingData, className, branding, onShareClick, shareTitle, shareUrl} = props
  const {savings} = deal
  const {businessLogoUrl: tenantImageLink, primaryColour: tenantColor} = branding
  const isTypeMerchandise = deal.type === DealType.Merchandise
  const isRewards = isRPDeal(deal)
  const showLimitedTimeBadge = savings?.boostedSavings && !isRewards

  const idNamespace = id("deal", props)
  const thumbnailImage = shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, branding) : null
  const boxShadowColor = determineDealColor(deal, tenantImageLink, tenantColor)

  const isUnavailable = isDealUnavailable(deal)

  const onClickDeal = () => {
    const source = page.toLowerCase()

    switch (page) {
      case DealPages.SEARCH:
        searchTracking.clickSearchResult(deal, isRewards)
        break
      default:
        tracking.clickPerk(deal, {...trackingData, source})
    }
  }

  const handleFavChange: FavouriteButtonOnChange = (isFavourite) =>
    isFavourite ? tracking.favouritePerk(deal, DEAL_PAGES.PERK) : tracking.unfavouritePerk(deal, DEAL_PAGES.PERK)

  return (
    <StyledDeal id={idNamespace} className={className}>
      {isTypeMerchandise && (
        <PromotionQueryComponent type={PromotionType.RewardChristmasMerchandise}>
          {({data: {promotionStatus} = {}}) =>
            promotionStatus?.enabled && <CardBadge text={<FormattedMessage id="perks.physicalProduct.offer.badge" />} />
          }
        </PromotionQueryComponent>
      )}
      <DealTileOnClick id={idNamespace} deal={deal} onClick={onClickDeal} isRewards={isRewards}>
        <CardImage deal={deal}>
          <Flex justifyContent="space-between" direction="column" position="relative" height="100%" padding={1.5}>
            <Row>
              <Col xs={6}>{thumbnailImage && <DealAvatar id={id("img-thumb", idNamespace)} src={thumbnailImage} small withShadow />}</Col>
              <Col xs={6}>
                {!isRPDeal(deal) && (
                  <Flex justifyContent="flex-end">
                    {onShareClick && (
                      <Spacing marginRight={1}>
                        <ShareIconButton
                          iconSize={20}
                          copyText={shareUrl}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            onShareClick(deal)
                          }}
                          sharingData={{
                            title: shareTitle,
                            url: shareUrl
                          }}
                        />
                      </Spacing>
                    )}
                    <FavouriteIconButton deal={deal} onChange={handleFavChange} iconSize={20} />
                  </Flex>
                )}
              </Col>
            </Row>
            {showLimitedTimeBadge && (
              <Flex justifyContent="flex-end">
                <Pill
                  text={<FormattedMessage id="perks.deals.boostedSaving.boostedAmount.text" />}
                  iconType="instant"
                  backgroundColor={mainTheme.colors.utility.acid}
                />
              </Flex>
            )}
          </Flex>
        </CardImage>
        <DealBody flush boxShadowColor={boxShadowColor}>
          <Spacing padding={[2, 1.5]}>
            {deal.brandName && (
              <H6
                marginBottom={mainTheme.spacing.half}
                color={isUnavailable ? mainTheme.colors.utility.subdued : mainTheme.colors.utility.contrast}>
                {deal.brandName.toUpperCase()}
              </H6>
            )}
            <DealHeader hasBrandName={!!deal.brandName} isTypeMerchandise={isTypeMerchandise} isUnavailable={isUnavailable}>
              {deal.header}
            </DealHeader>
            {isTypeMerchandise && (
              <Flex>
                <DealHighlightRow id={id("type", idNamespace)} type={DealHighlightTypes.MERCHANDISE} />
                <DealHighlightRow id={id("delivery", idNamespace)} type={DealHighlightTypes.SHIPPING_INCLUDED} />
              </Flex>
            )}
          </Spacing>
          <DealPromoInfo deal={deal as Required<IDeal>} />
        </DealBody>
      </DealTileOnClick>
    </StyledDeal>
  )
}

export default withBranding(Deal)
